import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

import { useThree, useFrame } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";
import { createThrottledPresenceSender } from "../utils/presenceSendRate";

export function CaptureHeadset({ currentObjectArtboard }) {
  const setCursor = useObjectStore((state) => state.setCursor);
  const sendPresenceUpdateToChannel = useObjectStore((state) => state.sendPresenceUpdateToChannel);
  const projectKey = useObjectStore((state) => state.projectKey);
  const realtimeToken = useObjectStore((state) => state.realtimeToken);
  const teleportCount = useObjectStore((state) => state.teleportCount);
  const { camera, mouse } = useThree()
  const xr = useXR();

  const { firstName } = useAccountStore()

  const buildUpdateObject = useCallback(() => ({
    field: "preview",
    updateType: "presenceUpdate",
    projectKey,
    realtime_token: realtimeToken,
    update: {
      preview: {
        rotation: new THREE.Euler().copy(camera.rotation),
        position: new THREE.Vector3(camera.position.x, 1.75, camera.position.z),
        mouse,
        page: "preview",
        device: "vr",
        artboard: currentObjectArtboard,
        name: firstName,
      },
    },
  }), [camera, currentObjectArtboard, firstName, mouse, projectKey, realtimeToken]);

  const presenceSender = useMemo(
    () => createThrottledPresenceSender((p) => sendPresenceUpdateToChannel(p)),
    [sendPresenceUpdateToChannel]
  );

  useEffect(() => {
    return () => presenceSender.cancel();
  }, [presenceSender]);

  useEffect(() => {
    if (xr.isPresenting) setCursor(null);
  }, [xr.isPresenting, setCursor]);

  useEffect(() => {
    if (!xr.isPresenting) return;
    presenceSender.force(buildUpdateObject());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- VR/context only, not every camera/mouse tick
  }, [xr.isPresenting, currentObjectArtboard, projectKey, firstName, realtimeToken, presenceSender]);

  useEffect(() => {
    if (!xr.isPresenting) return;
    presenceSender.force(buildUpdateObject());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- teleport bumps store; avoid deps that re-fire on every VR enter
  }, [teleportCount, presenceSender]);

  useFrame(() => {
    if (xr.isPresenting) {
      presenceSender.push(buildUpdateObject());
    }
  });

  return ('')
}

export function CaptureCamera({ projectKey, currentObjectArtboard }) {
  const sendPresenceUpdateToChannel = useObjectStore((state) => state.sendPresenceUpdateToChannel);
  const teleportCount = useObjectStore((state) => state.teleportCount);
  const { camera, raycaster, mouse } = useThree();
  const { realtimeToken } = useObjectStore();

  const useMouseMove = (onMouseMove) => {
    useEffect(() => {
      document.addEventListener("pointermove", onMouseMove);
      return () => {
        document.removeEventListener("pointermove", onMouseMove);
      };
    }, [onMouseMove]);
  };

  const { firstName } = useAccountStore();

  const presenceSender = useMemo(
    () => createThrottledPresenceSender((p) => sendPresenceUpdateToChannel(p)),
    [sendPresenceUpdateToChannel]
  );

  const ctxRef = useRef({});
  ctxRef.current = { projectKey, currentObjectArtboard, realtimeToken, firstName };

  const pointerDirty = useRef(false);
  const skipTeleportPresenceForce = useRef(true);
  const onPointerMove = useCallback(() => {
    pointerDirty.current = true;
  }, []);

  useMouseMove(onPointerMove);

  useFrame(() => {
    if (!pointerDirty.current) return;
    pointerDirty.current = false;
    const c = ctxRef.current;
    presenceSender.push({
      field: "preview",
      updateType: "presenceUpdate",
      projectKey: c.projectKey,
      realtime_token: c.realtimeToken,
      update: {
        preview: {
          rotation: camera.rotation,
          position: raycaster.ray.origin,
          mouse,
          page: "preview",
          device: "computer",
          artboard: c.currentObjectArtboard,
          name: c.firstName,
        },
      },
    });
  });

  useEffect(() => {
    return () => presenceSender.cancel();
  }, [presenceSender]);

  useEffect(() => {
    const c = ctxRef.current;
    presenceSender.force({
      field: "preview",
      updateType: "presenceUpdate",
      projectKey: c.projectKey,
      realtime_token: c.realtimeToken,
      update: {
        preview: {
          rotation: camera.rotation,
          position: raycaster.ray.origin,
          mouse,
          page: "preview",
          device: "computer",
          artboard: c.currentObjectArtboard,
          name: c.firstName,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- context only
  }, [projectKey, currentObjectArtboard, realtimeToken, firstName, presenceSender]);

  useEffect(() => {
    if (skipTeleportPresenceForce.current) {
      skipTeleportPresenceForce.current = false;
      return;
    }
    const c = ctxRef.current;
    presenceSender.force({
      field: "preview",
      updateType: "presenceUpdate",
      projectKey: c.projectKey,
      realtime_token: c.realtimeToken,
      update: {
        preview: {
          rotation: camera.rotation,
          position: raycaster.ray.origin,
          mouse,
          page: "preview",
          device: "computer",
          artboard: c.currentObjectArtboard,
          name: c.firstName,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- desktop teleport uses pointerup without pointermove
  }, [teleportCount, presenceSender]);

  return "";
}